import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Tabs, Tab, Spinner, Chip } from '@nextui-org/react';
import {
  Settings,
  Trophy,
  Users,
  Beer,
  ShoppingBag,
  Layers,
  MapPin,
  ChevronRight,
  Plus,
  Shield,
  Crown,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import userService from '../services/userService';

const TEAL = '#3FCED2';

type TabKey = 'tournaments' | 'series' | 'pubs' | 'clubs' | 'shops';

interface ManagedEntity {
  _id?: string | null;
  id?: string | null;
  slug?: string | null;
  name?: string | null;
  address?: any;
  image?: string | null;
  logo?: string | null;
  visible?: boolean | null;
  verified?: boolean | null;
}

function getId(e?: ManagedEntity | null): string | null {
  return (e?._id || e?.id) ?? null;
}

function formatAddress(addr?: any): string {
  if (!addr) return '';
  if (typeof addr === 'string') return addr;
  if (addr.label) return addr.label;
  const parts: string[] = [];
  if (addr.street) parts.push(addr.street);
  const cityLine = [addr.postcode, addr.city].filter(Boolean).join(' ');
  if (cityLine) parts.push(cityLine);
  if (addr.country) parts.push(addr.country);
  return parts.join(', ');
}

const ManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const initialTab = (searchParams.get('tab') as TabKey) || 'tournaments';
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);

  const [loading, setLoading] = useState(true);

  const [ownTournaments, setOwnTournaments] = useState<ManagedEntity[]>([]);
  const [ownSeries, setOwnSeries] = useState<ManagedEntity[]>([]);
  const [ownPubs, setOwnPubs] = useState<ManagedEntity[]>([]);
  const [ownClubs, setOwnClubs] = useState<ManagedEntity[]>([]);
  const [ownShops, setOwnShops] = useState<ManagedEntity[]>([]);

  const [adminTournaments, setAdminTournaments] = useState<ManagedEntity[]>([]);
  const [adminSeries, setAdminSeries] = useState<ManagedEntity[]>([]);
  const [adminPubs, setAdminPubs] = useState<ManagedEntity[]>([]);
  const [adminClubs, setAdminClubs] = useState<ManagedEntity[]>([]);
  const [adminShops, setAdminShops] = useState<ManagedEntity[]>([]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [
        oT, oSe, oP, oC, oSh,
        aT, aSe, aP, aC, aSh,
      ] = await Promise.all([
        userService.getOwnedTournaments(),
        userService.getOwnedSeries(),
        userService.getOwnedPubs(),
        userService.getOwnedClubs(),
        userService.getOwnedShops(),
        userService.getAdminTournaments(),
        userService.getAdminSeries(),
        userService.getAdminPubs(),
        userService.getAdminClubs(),
        userService.getAdminShops(),
      ]);
      setOwnTournaments(oT as ManagedEntity[]);
      setOwnSeries(oSe as ManagedEntity[]);
      setOwnPubs(oP as ManagedEntity[]);
      setOwnClubs(oC as ManagedEntity[]);
      setOwnShops(oSh as ManagedEntity[]);
      setAdminTournaments(aT as ManagedEntity[]);
      setAdminSeries(aSe as ManagedEntity[]);
      setAdminPubs(aP as ManagedEntity[]);
      setAdminClubs(aC as ManagedEntity[]);
      setAdminShops(aSh as ManagedEntity[]);
    } catch (err) {
      console.error('Fehler beim Laden der Verwaltung:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
    window.scrollTo({ top: 0 });
  }, [loadAll]);

  const handleTabChange = (key: React.Key) => {
    const k = String(key) as TabKey;
    setActiveTab(k);
    setSearchParams({ tab: k }, { replace: true });
  };

  // Merge owned + admin (admin shown with badge)
  const mergedByTab = useMemo(() => {
    const merge = (owned: ManagedEntity[], adminList: ManagedEntity[]) => {
      const ownedIds = new Set(owned.map(getId).filter(Boolean));
      const adminOnly = adminList.filter((e) => {
        const id = getId(e);
        return id && !ownedIds.has(id);
      });
      return {
        owned,
        adminOnly,
        total: owned.length + adminOnly.length,
      };
    };
    return {
      tournaments: merge(ownTournaments, adminTournaments),
      series: merge(ownSeries, adminSeries),
      pubs: merge(ownPubs, adminPubs),
      clubs: merge(ownClubs, adminClubs),
      shops: merge(ownShops, adminShops),
    };
  }, [
    ownTournaments, adminTournaments,
    ownSeries, adminSeries,
    ownPubs, adminPubs,
    ownClubs, adminClubs,
    ownShops, adminShops,
  ]);

  const totalCount =
    mergedByTab.tournaments.total +
    mergedByTab.series.total +
    mergedByTab.pubs.total +
    mergedByTab.clubs.total +
    mergedByTab.shops.total;

  const detailRoute = (tab: TabKey, e: ManagedEntity): string => {
    const id = getId(e) || '';
    const slugOrId = e.slug || id;
    switch (tab) {
      case 'tournaments': return id ? `/verwaltung/turnier/${id}/bearbeiten` : `/turnier/${slugOrId}`;
      case 'series': return id ? `/verwaltung/turnierserie/${id}/bearbeiten` : `/turnierserie/${slugOrId}`;
      case 'pubs': return id ? `/verwaltung/spielstaette/${id}/bearbeiten` : `/spielstaette/${slugOrId}`;
      case 'clubs': return id ? `/verwaltung/verein/${id}/bearbeiten` : `/verein/${slugOrId}`;
      case 'shops': return id ? `/verwaltung/shop/${id}/bearbeiten` : `/shop/${slugOrId}`;
    }
  };

  const createRoute = (tab: TabKey): string | null => {
    switch (tab) {
      case 'clubs': return '/verwaltung/verein/erstellen';
      case 'pubs': return '/verwaltung/spielstaette/erstellen';
      case 'shops': return '/verwaltung/shop/erstellen';
      case 'series': return '/verwaltung/turnierserie/erstellen';
      case 'tournaments': return '/verwaltung/turnier/erstellen';
      default: return null;
    }
  };

  const renderList = (tab: TabKey) => {
    const data = mergedByTab[tab];
    const labels: Record<TabKey, { single: string; plural: string; icon: React.ReactNode }> = {
      tournaments: { single: 'Turnier', plural: 'Turniere', icon: <Trophy className="w-10 h-10" /> },
      series: { single: 'Turnierserie', plural: 'Turnierserien', icon: <Layers className="w-10 h-10" /> },
      pubs: { single: 'Spielstätte', plural: 'Spielstätten', icon: <Beer className="w-10 h-10" /> },
      clubs: { single: 'Verein', plural: 'Vereine', icon: <Users className="w-10 h-10" /> },
      shops: { single: 'Shop', plural: 'Shops', icon: <ShoppingBag className="w-10 h-10" /> },
    };
    const meta = labels[tab];

    const cRoute = createRoute(tab);

    if (data.total === 0) {
      return (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-2xl py-16 px-6 text-center">
          <div className="inline-flex w-16 h-16 rounded-full bg-zinc-800/60 items-center justify-center text-zinc-500 mb-4">
            {meta.icon}
          </div>
          <h3 className="text-lg font-semibold mb-1">Keine {meta.plural}</h3>
          <p className="text-sm text-zinc-400 mb-4">
            Du verwaltest aktuell keine {meta.plural}.
          </p>
          {cRoute && (
            <button
              onClick={() => navigate(cRoute)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium"
              style={{ backgroundColor: TEAL, color: '#000' }}
            >
              <Plus className="w-4 h-4" /> {meta.single} erstellen
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {cRoute && (
          <div className="flex justify-end">
            <button
              onClick={() => navigate(cRoute)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium"
              style={{ backgroundColor: TEAL, color: '#000' }}
            >
              <Plus className="w-4 h-4" /> {meta.single} erstellen
            </button>
          </div>
        )}
        {data.owned.length > 0 && (
          <Section
            title="Eigene"
            icon={<Crown className="w-4 h-4" />}
            color="#FFD700"
            count={data.owned.length}
          >
            <EntityList items={data.owned} ownerType="owner" onOpen={(e) => navigate(detailRoute(tab, e))} />
          </Section>
        )}
        {data.adminOnly.length > 0 && (
          <Section
            title="Als Admin"
            icon={<Shield className="w-4 h-4" />}
            color={TEAL}
            count={data.adminOnly.length}
          >
            <EntityList items={data.adminOnly} ownerType="admin" onOpen={(e) => navigate(detailRoute(tab, e))} />
          </Section>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Verwaltung - DartCloud</title>
        <meta name="description" content="Verwalte deine Turniere, Vereine, Spielstätten, Shops und Turnierserien" />
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${TEAL}22` }}
          >
            <Settings className="w-5 h-5" style={{ color: TEAL }} />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold">Verwaltung</h1>
            <p className="text-sm text-zinc-400">
              {user?.username ? `Hallo ${user.username}, ` : ''}
              {totalCount} {totalCount === 1 ? 'Eintrag' : 'Einträge'} verwaltbar
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <Tabs
            selectedKey={activeTab}
            onSelectionChange={handleTabChange}
            variant="solid"
            classNames={{
              tabList: 'bg-zinc-900/70 border border-zinc-800 p-1 rounded-xl flex-wrap',
              cursor: 'bg-zinc-800',
              tab: 'text-zinc-300 data-[selected=true]:text-white',
              tabContent: 'group-data-[selected=true]:text-white',
            }}
          >
            <Tab key="tournaments" title={<TabLabel icon={<Trophy className="w-4 h-4" />} label="Turniere" count={mergedByTab.tournaments.total} />} />
            <Tab key="series" title={<TabLabel icon={<Layers className="w-4 h-4" />} label="Serien" count={mergedByTab.series.total} />} />
            <Tab key="pubs" title={<TabLabel icon={<Beer className="w-4 h-4" />} label="Spielstätten" count={mergedByTab.pubs.total} />} />
            <Tab key="clubs" title={<TabLabel icon={<Users className="w-4 h-4" />} label="Vereine" count={mergedByTab.clubs.total} />} />
            <Tab key="shops" title={<TabLabel icon={<ShoppingBag className="w-4 h-4" />} label="Shops" count={mergedByTab.shops.total} />} />
          </Tabs>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Spinner color="primary" size="lg" />
          </div>
        ) : (
          renderList(activeTab)
        )}
      </div>
    </div>
  );
};

const TabLabel: React.FC<{ icon: React.ReactNode; label: string; count: number }> = ({ icon, label, count }) => (
  <div className="flex items-center gap-2 px-1 py-0.5">
    {icon}
    <span className="hidden sm:inline">{label}</span>
    <span
      className="text-xs px-1.5 py-0.5 rounded-full"
      style={{ backgroundColor: `${TEAL}22`, color: TEAL }}
    >
      {count}
    </span>
  </div>
);

const Section: React.FC<{
  title: string;
  icon: React.ReactNode;
  color: string;
  count: number;
  children: React.ReactNode;
}> = ({ title, icon, color, count, children }) => (
  <div>
    <div className="flex items-center gap-2 mb-3">
      <div
        className="w-7 h-7 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: `${color}22`, color }}
      >
        {icon}
      </div>
      <h2 className="text-base font-semibold">{title}</h2>
      <Chip size="sm" variant="flat" style={{ backgroundColor: `${color}22`, color }}>
        {count}
      </Chip>
    </div>
    {children}
  </div>
);

const EntityList: React.FC<{
  items: ManagedEntity[];
  ownerType: 'owner' | 'admin';
  onOpen: (e: ManagedEntity) => void;
}> = ({ items, ownerType, onOpen }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
    {items.map((e) => {
      const id = getId(e) || e.slug || Math.random().toString();
      const address = formatAddress(e.address);
      return (
        <button
          key={id}
          onClick={() => onOpen(e)}
          className="text-left bg-zinc-900/60 hover:bg-zinc-900 border border-zinc-800 hover:border-zinc-700 rounded-xl p-4 transition-colors flex items-center gap-3 group"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold truncate group-hover:text-white">{e.name || 'Ohne Name'}</span>
              {e.verified && (
                <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${TEAL}22`, color: TEAL }}>
                  Verifiziert
                </span>
              )}
              {e.visible === false && (
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-zinc-800 text-zinc-400">
                  Versteckt
                </span>
              )}
            </div>
            {address && (
              <div className="text-xs text-zinc-400 truncate flex items-center gap-1 mt-1">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                {address}
              </div>
            )}
            <div className="text-xs text-zinc-500 mt-1">
              {ownerType === 'owner' ? 'Eigentümer' : 'Admin-Rechte'}
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-zinc-500 flex-shrink-0 group-hover:text-zinc-300" />
        </button>
      );
    })}
  </div>
);

export default ManagementPage;
